import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// An early-war armoured car: a riveted field-grey slab body bolted over a
// touring-car chassis on four spoked wheels, with angled engine plates over
// the bonnet and a small rotating-look turret carrying a machine gun. Faces
// +Z, fits one tile.

const GREY = "#6d6f64";
const GUNMETAL = "#4a4d52";
const TYRE = "#33342f";
const RUST = "#7a4a30";

function box(
  w: number, h: number, d: number, color: string,
  position: [number, number, number], rotation?: [number, number, number],
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation };
}

function cyl(
  r: number, h: number, seg: number, color: string,
  position: [number, number, number], rotation?: [number, number, number],
): Part {
  return { geometry: new THREE.CylinderGeometry(r, r, h, seg), color, position, rotation };
}

// A spoked wheel standing in the y-z plane (axle along x), touching y = 0.
function wheel(x: number, z: number): Part[] {
  const r = 0.12;
  return [
    {
      geometry: new THREE.TorusGeometry(r - 0.024, 0.024, 5, 12),
      color: TYRE, position: [x, r, z], rotation: [0, Math.PI / 2, 0],
    },
    cyl(0.032, 0.06, 8, GUNMETAL, [x, r, z], [0, 0, Math.PI / 2]),
    box(0.02, 0.19, 0.02, RUST, [x, r, z], [Math.PI / 4, 0, 0]),
    box(0.02, 0.19, 0.02, RUST, [x, r, z], [-Math.PI / 4, 0, 0]),
  ];
}

function parts(): Part[] {
  const out: Part[] = [
    ...wheel(0.20, 0.26), ...wheel(-0.20, 0.26),
    ...wheel(0.20, -0.26), ...wheel(-0.20, -0.26),

    // axles
    cyl(0.018, 0.42, 6, GUNMETAL, [0, 0.12, 0.26], [0, 0, Math.PI / 2]),
    cyl(0.018, 0.42, 6, GUNMETAL, [0, 0.12, -0.26], [0, 0, Math.PI / 2]),

    // chassis frame
    box(0.28, 0.05, 0.70, GUNMETAL, [0, 0.16, 0]),

    // armoured hull: slab fighting compartment over the rear two thirds
    box(0.34, 0.17, 0.52, GREY, [0, 0.275, -0.09]),
    box(0.28, 0.13, 0.34, GREY, [0, 0.42, -0.15]),
    // rear plate sloping down over the back
    box(0.28, 0.03, 0.16, GREY, [0, 0.42, -0.345], [-0.7, 0, 0]),

    // engine bonnet with angled plates meeting at a ridge
    box(0.30, 0.10, 0.24, GREY, [0, 0.235, 0.24]),
    box(0.155, 0.025, 0.24, GREY, [0.083, 0.315, 0.24], [0, 0, -0.55]),
    box(0.155, 0.025, 0.24, GREY, [-0.083, 0.315, 0.24], [0, 0, 0.55]),
    // armoured radiator plate flush with the bonnet face
    box(0.24, 0.10, 0.03, GUNMETAL, [0, 0.235, 0.365]),

    // mudguard fenders over the wheels
    box(0.10, 0.02, 0.72, GREY, [0.225, 0.265, 0]),
    box(0.10, 0.02, 0.72, GREY, [-0.225, 0.265, 0]),

    // the small turret: drum, domed cap and a water-cooled MG
    cyl(0.115, 0.11, 12, GREY, [0, 0.54, -0.15]),
    cyl(0.085, 0.045, 12, GREY, [0, 0.615, -0.15]),
    // MG jacket + barrel poking out the turret front
    cyl(0.022, 0.10, 6, GUNMETAL, [0, 0.56, -0.02], [Math.PI / 2, 0, 0]),
    cyl(0.012, 0.10, 6, GUNMETAL, [0, 0.56, 0.07], [Math.PI / 2, 0, 0]),
  ];

  // rivet studs along the hull side seam and the turret base
  const rivets: Array<[number, number, number]> = [
    [0.175, 0.34, 0.08], [0.175, 0.34, -0.26],
    [-0.175, 0.34, 0.08], [-0.175, 0.34, -0.26],
    [0.10, 0.49, -0.15], [-0.10, 0.49, -0.15],
  ];
  for (const p of rivets) out.push(box(0.02, 0.02, 0.02, GUNMETAL, p));

  return out;
}

export const armoredCar = defineModel({
  id: "armored-car",
  name: "Armoured Car",
  tags: ["vehicle", "wwi", "military", "armoured-car", "car", "low-poly"],
  batchId: "wwi",
  summary: "An early armoured car with a slab body and an MG turret.",
  description:
    "An early-war armoured car: a riveted field-grey slab body on a " +
    "touring-car chassis with four spoked wheels, angled engine plates over " +
    "the bonnet, an armoured radiator plate and a small turret with a " +
    "water-cooled machine gun. Faces +Z, fits one tile.",
  author: "Fable 5",
  footprint: 0.5,
  tiles: { w: 1, h: 1 },
  parts,
  animations: [],
});
